import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AppLayout } from './components/Layout'; // Use the new AppLayout
import { HomePage } from './pages/HomePage';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { DashboardPage } from './pages/DashboardPage';
import { FilesPage } from './pages/FilesPage';
import { SettingsPage } from './pages/SettingsPage';
import { DownloadPage } from './pages/DownloadPage';
import { DownloadHandlerPage } from './pages/DownloadHandlerPage';
import ProductManagementPage from './pages/ProductManagementPage';
import S3ConfigPage from './pages/S3ConfigPage';
import UserManagementPage from './pages/UserManagementPage';
import EmailPage from './pages/EmailPage'; // New import
import GetTableColumnPage from './pages/GetTableColumnPage';
import ToXMLPage from './pages/ToXMLPage';
import { Spin } from 'antd';
import RedditCrawlerPage from './pages/RedditCrawlerPage';
import { useEffect } from 'react';
import { fetchCsrfToken } from './utils/api';
import SqlCollectorPage from './pages/SqlCollectorPage';
import GuduSalesPage from './pages/GuduSalesPage';
import { SQLFlowDeployPage } from './pages/sqlflow-deploy';
import SelfServiceLicensePage from './pages/sqlflow-deploy/SelfServiceLicensePage';
import TrialLicensePage from './pages/sqlflow-deploy/TrialLicensePage';

// Import Marketing components
import {
	MarketingLayout,
	MarketingDashboard,
	ContactsPage,
	AudiencesPage,
	CampaignsPage,
	CampaignComposer,
	UnsubscribersPage,
} from './pages/marketing';
import DocumentationViewerSimple from './pages/marketing/DocumentationViewerSimple';

// A wrapper for protected routes
const ProtectedRoute = () => {
	const { isAuthenticated, isLoading } = useAuth();
	if (isLoading) {
		return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><Spin size="large" /></div>;
	}
	return isAuthenticated ? <AppLayout><Outlet /></AppLayout> : <Navigate to="/login" />;
};

const PublicPageLayout = () => {
	return (
		<AppLayout>
			<Outlet />
		</AppLayout>
	);
};

function App() {
	useEffect(() => {
		// Fetch CSRF token when the app loads
		const initializeCsrfToken = async () => {
			try {
				await fetchCsrfToken();
			} catch (error) {
				console.error('Failed to initialize CSRF token:', error);
			}
		};
		initializeCsrfToken();
	}, []);

	return (
		<Router>
			<AuthProvider>
				<Routes>
					<Route path="/" element={<HomePage />} />
					<Route path="/login" element={<LoginPage />} />
					<Route path="/register" element={<RegisterPage />} />
					<Route path="/download/:fileId" element={<DownloadHandlerPage />} />
					<Route path="/self-service-license/:linkId" element={<SelfServiceLicensePage />} />
					<Route path="/trial-license" element={<TrialLicensePage />} />

					<Route element={<PublicPageLayout />}>
						<Route path="/get-table-column" element={<GetTableColumnPage />} />
						<Route path="/to-xml" element={<ToXMLPage />} />
						<Route path="/download" element={<DownloadPage />} />
						<Route path="/download-handler" element={<DownloadHandlerPage />} />
						<Route path="/settings" element={<SettingsPage />} />
					</Route>

					{/* Protected Routes */}
					<Route element={<ProtectedRoute />}>
						<Route path="/dashboard" element={<DashboardPage />} />
						<Route path="/files" element={<FilesPage />} />
						<Route path="/settings" element={<SettingsPage />} />
						<Route path="/products" element={<ProductManagementPage />} />
						<Route path="/users" element={<UserManagementPage />} />
						<Route path="/s3-configs" element={<S3ConfigPage />} />
						<Route path="/email" element={<EmailPage />} />
						<Route path="/downloads" element={<DownloadPage />} />
						<Route path="/reddit-crawler" element={<RedditCrawlerPage />} />
						<Route path="/sql-collector" element={<SqlCollectorPage />} />
						<Route path="/gudu-sales" element={<GuduSalesPage />} />
						<Route path="/sqlflow-deploy" element={<SQLFlowDeployPage />} />

						{/* Marketing Routes */}
						<Route path="/marketing" element={<MarketingLayout />}>
							<Route index element={<Navigate to="dashboard" />} />
							<Route path="dashboard" element={<MarketingDashboard />} />
							<Route path="contacts" element={<ContactsPage />} />
							<Route path="audiences" element={<AudiencesPage />} />
							<Route path="campaigns" element={<CampaignsPage />} />
							<Route path="campaigns/create" element={<CampaignComposer />} />
							<Route path="campaigns/:id/edit" element={<CampaignComposer />} />
							<Route path="campaigns/:id" element={<CampaignComposer />} />
							<Route path="unsubscribers" element={<UnsubscribersPage />} />
							<Route path="docs" element={<DocumentationViewerSimple />} />
						</Route>
					</Route>

				</Routes>
			</AuthProvider>
		</Router>
	);
}

export default App;